import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api } from '../lib/api'

const box = { background: '#161d31', borderRadius: 12, padding: 16, marginBottom: 16, color: '#f8fafc' }
const btn = { background: '#4f46e5', color: 'white', border: 0, borderRadius: 8, padding: '10px 14px', cursor: 'pointer' }
const subtleBtn = { ...btn, background: '#334155' }
const badgeBase = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '5px 10px',
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 600,
  border: '1px solid transparent',
}

function modeBadge(quiz) {
  if (quiz.generated_template_type === 'rule') return { text: 'Generated quiz', style: { background: '#312e81', color: '#c7d2fe', borderColor: '#4f46e5' } }
  if (quiz.generated_template_type === 'fixed') return { text: 'From fixed template', style: { background: '#0f766e', color: '#ccfbf1', borderColor: '#14b8a6' } }
  return { text: 'Manual quiz', style: { background: '#1f2937', color: '#cbd5e1', borderColor: '#475569' } }
}

const exportCards = {
  gift: {
    title: 'GIFT export',
    button: 'Export as GIFT',
    description: 'Best for Moodle import or other systems that understand GIFT format.',
  },
  answer_key: {
    title: 'Answer key PDF',
    button: 'Export Answer Key (PDF)',
    description: 'Printable reference with correct answers shown for each question.',
  },
  student_handout: {
    title: 'Student handout PDF',
    button: 'Export Student Handout (PDF)',
    description: 'Printable student-facing version without answers.',
  },
}

export default function QuizExportPage() {
  const { id } = useParams()
  const [quiz, setQuiz] = useState(null)
  const [loading, setLoading] = useState(true)
  const [working, setWorking] = useState('')
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [exports, setExports] = useState({})

  useEffect(() => {
    loadQuiz()
  }, [id])

  async function loadQuiz() {
    setLoading(true)
    setError('')
    try {
      const data = await api.quiz(id)
      setQuiz(data)
    } catch (err) {
      setError(String(err))
    } finally {
      setLoading(false)
    }
  }

  async function runExport(kind) {
    setWorking(kind)
    setError('')
    setMessage('')
    try {
      let result
      if (kind === 'gift') result = await api.exportQuizGift(id)
      if (kind === 'answer_key') result = await api.exportQuizAnswerKey(id)
      if (kind === 'student_handout') result = await api.exportQuizStudentHandout(id)
      setExports(current => ({ ...current, [kind]: result }))
      setMessage(`Created ${result.file_name}`)
      if (result.download_url) {
        window.open(result.download_url, '_blank', 'noopener,noreferrer')
      }
    } catch (err) {
      setError(String(err))
    } finally {
      setWorking('')
    }
  }

  if (loading) return <div style={{ padding: 20 }}>Loading...</div>
  if (error && !quiz) return <div style={{ padding: 20 }}>Error: {error}</div>
  if (!quiz) return <div style={{ padding: 20 }}>Quiz not found</div>

  const mode = modeBadge(quiz)
  const hasSnapshots = quiz.snapshot_count > 0

  return (
    <div style={{ padding: 20, maxWidth: 900 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 style={{ margin: 0 }}>Export: {quiz.title}</h1>
        <Link to={`/quizzes/${id}`} style={{ color: '#93c5fd', textDecoration: 'none', fontSize: 14 }}>← Back to quiz</Link>
      </div>

      <div style={box}>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>{quiz.title}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
          <span style={{ ...badgeBase, ...mode.style }}>{mode.text}</span>
          {hasSnapshots && (
            <span style={{ ...badgeBase, background: '#14532d', color: '#bbf7d0', borderColor: '#16a34a' }}>
              Frozen snapshots · {quiz.snapshot_count}
            </span>
          )}
        </div>
        {quiz.generated_template_name && (
          <div style={{ color: '#cbd5e1', marginBottom: 8, fontSize: 14 }}>
            Source template: <strong>{quiz.generated_template_name}</strong>
          </div>
        )}
        {quiz.description && <div style={{ color: '#94a3b8', marginBottom: 8, fontSize: 14 }}>{quiz.description}</div>}
        <div style={{ color: '#94a3b8', fontSize: 13 }}>Status: <strong style={{ color: '#f8fafc' }}>{quiz.status}</strong></div>
      </div>

      {hasSnapshots ? (
        <div style={{ ...box, background: '#052e16', borderLeft: '4px solid #22c55e' }}>
          <div style={{ fontWeight: 600, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
            🔒 Exports are frozen for this quiz
          </div>
          <p style={{ margin: 0, color: '#dcfce7', fontSize: 14 }}>
            This quiz has {quiz.snapshot_count} frozen {quiz.snapshot_count === 1 ? 'snapshot' : 'snapshots'}. GIFT and PDF exports use the saved snapshot content even if the source questions change later.
          </p>
        </div>
      ) : (
        <div style={{ ...box, background: '#1e293b', borderLeft: '4px solid #64748b' }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Exports use current question content</div>
          <p style={{ margin: 0, color: '#cbd5e1', fontSize: 14 }}>
            This quiz does not have frozen snapshots, so exports reflect the current state of the linked questions.
          </p>
        </div>
      )}

      {error && !working ? (
        <div style={{ ...box, background: '#450a0a', borderLeft: '4px solid #dc2626' }}>
          <div style={{ color: '#fca5a5', fontWeight: 500, marginBottom: 8 }}>Export failed</div>
          <pre style={{ margin: 0, color: '#fca5a5', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{error}</pre>
        </div>
      ) : null}

      <div style={box}>
        <h3 style={{ marginTop: 0, marginBottom: 16 }}>Export Formats</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {Object.entries(exportCards).map(([kind, config]) => (
            <div key={kind} style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 10, padding: 14 }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>{config.title}</div>
              <div style={{ marginBottom: 12, color: '#94a3b8', fontSize: 13 }}>{config.description}</div>
              <button style={btn} onClick={() => runExport(kind)} disabled={!!working}>
                {working === kind ? 'Exporting…' : config.button}
              </button>
            </div>
          ))}
        </div>
        {message ? <div style={{ marginTop: 16, background: '#052e16', color: '#86efac', padding: 12, borderRadius: 6, fontSize: 14 }}>✓ {message}</div> : null}
      </div>

      {Object.entries(exports).length > 0 ? (
        <div style={box}>
          <h3 style={{ marginTop: 0, marginBottom: 12 }}>Generated Files</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {Object.entries(exports).map(([kind, item]) => (
              <div key={kind} style={{ background: '#0f172a', padding: 12, borderRadius: 6, border: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>{kind.replace(/_/g, ' ')}</div>
                  <div style={{ color: '#94a3b8', fontSize: 12 }}>{item.file_name}</div>
                </div>
                {item.download_url ? (
                  <a href={item.download_url} target="_blank" rel="noreferrer" style={{ ...btn, padding: '6px 12px', fontSize: 12, textDecoration: 'none' }}>
                    Download
                  </a>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div style={{ marginTop: 20, display: 'flex', gap: 8 }}>
        <Link to="/quizzes" style={{ ...btn, textDecoration: 'none' }}>← Back to quizzes</Link>
        <Link to={`/quizzes/${id}`} style={{ ...subtleBtn, textDecoration: 'none' }}>View quiz</Link>
      </div>
    </div>
  )
}
